import * as THREE from 'three';
import {
    getAdjustableLegBoxes,
    getAdjustableLegFrontDepth,
    isAdjustableLeg,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {computePlanDistance} from 'components/customer/RoomPlanner/helpers/snapping/base';
import {
    DEFAULT_SNAPPING_DISTANCE,
    KICK_PANEL_SETBACK_MM,
    SCENE_UNITS_PER_MM,
} from 'components/customer/RoomPlanner/helpers/snapping/constants';
import {
    SnapCandidate,
    SnapStrategy,
} from 'components/customer/RoomPlanner/helpers/snapping/types';

/**
 * Where kick-length panels rest (R-12, CO-5716): under a cabinet's front, in
 * the inside corner of an L-shaped corner base, and at a right angle to the
 * end of another kick-length panel. Every function here works in the floor
 * plane on quarter-turn axes and leaves height to the caller.
 */

// A box's extent along a quarter-turn unit vector in the floor plane.
export const extentAlong = (box: THREE.Box3, direction: THREE.Vector3) => {
    const a = box.min.dot(direction);
    const b = box.max.dot(direction);

    return [Math.min(a, b), Math.max(a, b)] as const;
};

// The way a cabinet's doors face.
const frontOf = (cabinet: THREE.Object3D) =>
    new THREE.Vector3(
        Math.round(Math.sin(cabinet.rotation.z)),
        0,
        Math.round(Math.cos(cabinet.rotation.z))
    );

// The product's box if its origin were moved to `landing`.
const boxAt = (product: THREE.Object3D, landing: THREE.Vector3) =>
    new THREE.Box3()
        .setFromObject(product)
        .translate(landing.clone().sub(product.position));

// The origin that puts the product's box minimum at `minU` along `u` and
// `minV` along `v`.
const landingForBoxMin = (
    product: THREE.Object3D,
    u: THREE.Vector3,
    minU: number,
    v: THREE.Vector3,
    minV: number
) => {
    const box = new THREE.Box3().setFromObject(product);

    return product.position
        .clone()
        .addScaledVector(u, minU - extentAlong(box, u)[0])
        .addScaledVector(v, minV - extentAlong(box, v)[0]);
};

// A kick-length panel's thickness and length in plan.
const panelExtents = (panel: THREE.Object3D) => {
    const size = new THREE.Box3()
        .setFromObject(panel)
        .getSize(new THREE.Vector3());

    return {
        thickness: Math.min(size.x, size.z),
        runsAlongX: size.x > size.z,
    };
};

/**
 * Sets the depth of a kick-length panel running along a cabinet's front:
 * its back flush against the front of the cabinet's adjustable legs, or, with
 * no legs, its face KICK_PANEL_SETBACK_MM behind the carcase face. The
 * carcase face is the plane through the cabinet's origin facing its front.
 * End panels (running front to back) and panels behind the cabinet's back
 * are left where the positioning put them.
 * @param {THREE.Group} panel The kick-length panel.
 * @param {THREE.Group} cabinet The cabinet it sits under.
 * @param {THREE.Vector3} landing Where the positioning put the panel's
 * origin; moved in place.
 */
export const setKickPanelDepth = (
    panel: THREE.Group,
    cabinet: THREE.Group,
    landing: THREE.Vector3
) => {
    const front = frontOf(cabinet);
    const across = new THREE.Vector3(front.z, 0, -front.x);
    const panelBox = boxAt(panel, landing);
    const [panelBack, panelFront] = extentAlong(panelBox, front);
    const [panelStart, panelEnd] = extentAlong(panelBox, across);

    if (panelFront - panelBack >= panelEnd - panelStart) return;

    const carcaseFront = cabinet.position.dot(front);
    const [cabinetBack] = extentAlong(
        new THREE.Box3().setFromObject(cabinet),
        front
    );
    const panelMiddle = (panelBack + panelFront) / 2;

    if (carcaseFront - panelMiddle > panelMiddle - cabinetBack) return;

    const legFront = getAdjustableLegFrontDepth(cabinet, front);
    const shift =
        legFront !== null
            ? legFront - panelBack
            : carcaseFront -
              KICK_PANEL_SETBACK_MM * SCENE_UNITS_PER_MM -
              panelFront;

    landing.addScaledVector(front, shift);
};

/**
 * Where a kicker frame rests under a cabinet that faces the same way (R-12):
 * across, on the cabinet's origin, moved in by a fifth of the exterior
 * thickness when the kicker has a finished left edge; front to back, by the
 * difference in depth between the two, recessed by a fifth of the exterior
 * thickness for a finished front edge and again under a panel product. Worked
 * in the cabinet's own frame, so it holds whichever way the run faces.
 * @param {THREE.Group} kicker The kicker frame, turned the same way as the
 * cabinet.
 * @param {THREE.Group} cabinet The cabinet above it.
 * @param {number} exteriorThickness The kicker's exterior thickness, mm.
 * @return {THREE.Vector3} The kicker's resting origin, at its own height.
 */
export const getKickerFrameLanding = (
    kicker: THREE.Group,
    cabinet: THREE.Group,
    exteriorThickness: number
) => {
    const front = frontOf(cabinet);
    const across = new THREE.Vector3(front.z, 0, -front.x);
    const depthOf = (group: THREE.Group) => {
        const [back, face] = extentAlong(
            new THREE.Box3().setFromObject(group),
            front
        );

        return face - back;
    };
    const edge = kicker.userData.kickerFrameEdge as
        {left?: boolean; front?: boolean} | undefined;
    const recess = exteriorThickness * SCENE_UNITS_PER_MM;
    let alongFront = depthOf(kicker) - depthOf(cabinet);

    if (edge?.front) alongFront -= recess;
    if (cabinet.userData.isPanelProduct) alongFront -= recess;

    return cabinet.position
        .clone()
        .addScaledVector(across, edge?.left ? recess : 0)
        .addScaledVector(front, alongFront)
        .setY(kicker.position.y);
};

/**
 * Whether a kick-length panel at `landing` would sit inside a kicker frame or
 * another kick-length panel, which already fills the toe kick there. Kicker
 * frames carry ignoreOverlap, so the drag's overlap checks don't catch them.
 * The other product's box is inset by one unit, as the drag's after-snap
 * check insets cabinets, so products meeting end to end or face to end fit.
 * @param {THREE.Group} panel The kick-length panel.
 * @param {THREE.Vector3} landing Where the panel's origin would go.
 * @param {THREE.Group[]} products Every product in the room.
 * @return {boolean} True when a floor product is in the way.
 */
export const landingMeetsFloorProduct = (
    panel: THREE.Group,
    landing: THREE.Vector3,
    products: THREE.Group[]
) => {
    const panelBox = boxAt(panel, landing);

    return products.some(
        (product) =>
            (product.userData.isFloorProduct ||
                product.userData.isKickerLengthProduct) &&
            product.userData.id !== panel.userData.id &&
            panelBox.intersectsBox(
                new THREE.Box3().setFromObject(product).expandByScalar(-1)
            )
    );
};

// Animated doors and drawer fronts are tagged by the animation handler
// (Preview3DCommon/animations/handler.ts); they stand proud of the carcase.
const isDoorOrDrawerPart = (node: THREE.Object3D, root: THREE.Object3D) => {
    let current: THREE.Object3D | null = node;

    while (current && current !== root) {
        if (current.userData.boundsExcludeAnimatedDoorOrDrawerPart === true)
            return true;
        current = current.parent;
    }

    return false;
};

interface Rect {
    out: readonly [number, number];
    front: readonly [number, number];
}

/**
 * The inside corner of an L-shaped corner base, from its carcase meshes
 * (legs, doors and drawer fronts left out), in the cabinet's own frame.
 * Face A is the front of the leg that runs across; face B is the notch face
 * of the leg that runs front to back. Both are given as planes: face A along
 * `front`, face B along `out`, which points out of the notch.
 *
 * The shared `depth` is how far both panels' outer faces sit behind their
 * faces: the shallower of the two leg-clear setbacks (each puts a panel's
 * back flush on the front-most leg behind that face), so neither panel
 * passes through a leg; KICK_PANEL_SETBACK_MM without legs.
 * @param {THREE.Group} cabinet The L-shaped corner base.
 * @param {number} thickness The kick-length panel's thickness.
 * @return {object|null} The frame, faces and depth, or null when no notch
 * is found.
 */
export const getLShapeCorner = (cabinet: THREE.Group, thickness: number) => {
    const front = frontOf(cabinet);
    const across = new THREE.Vector3(front.z, 0, -front.x);
    const legBoxes = getAdjustableLegBoxes(cabinet);
    const carcase: THREE.Box3[] = [];

    cabinet.updateWorldMatrix(true, true);
    cabinet.traverse((child) => {
        if (!(child as THREE.Mesh).isMesh || isAdjustableLeg(child)) return;
        if (isDoorOrDrawerPart(child, cabinet)) return;
        carcase.push(new THREE.Box3().setFromObject(child));
    });
    if (!carcase.length) return null;

    const rectsFor = (out: THREE.Vector3): Rect[] =>
        carcase.map((box) => ({
            out: extentAlong(box, out),
            front: extentAlong(box, front),
        }));
    const covers = (rects: Rect[], o: number, f: number) =>
        rects.some(
            (rect) =>
                rect.out[0] <= o &&
                o <= rect.out[1] &&
                rect.front[0] <= f &&
                f <= rect.front[1]
        );
    const probe = 0.5;
    const acrossRects = rectsFor(across);
    const frontEdge = Math.max(...acrossRects.map((rect) => rect.front[1]));
    const acrossEnd = Math.max(...acrossRects.map((rect) => rect.out[1]));
    const acrossStart = Math.min(...acrossRects.map((rect) => rect.out[0]));
    let out: THREE.Vector3;

    if (!covers(acrossRects, acrossEnd - probe, frontEdge - probe)) {
        out = across;
    } else if (!covers(acrossRects, acrossStart + probe, frontEdge - probe)) {
        out = across.clone().negate();
    } else {
        return null;
    }

    const rects = rectsFor(out);
    const notchEdge = Math.max(...rects.map((rect) => rect.out[1]));
    const frontRow = rects.filter(
        (rect) =>
            rect.front[0] <= frontEdge - probe &&
            frontEdge - probe <= rect.front[1]
    );
    const notchColumn = rects.filter(
        (rect) =>
            rect.out[0] <= notchEdge - probe && notchEdge - probe <= rect.out[1]
    );

    if (!frontRow.length || !notchColumn.length) return null;

    const faceB = Math.max(...frontRow.map((rect) => rect.out[1]));
    const faceA = Math.max(...notchColumn.map((rect) => rect.front[1]));

    if (faceB >= notchEdge || faceA >= frontEdge) return null;

    const legFrontA = legBoxes
        .filter(
            (leg) =>
                extentAlong(leg, out)[1] > faceB &&
                extentAlong(leg, out)[0] < notchEdge &&
                extentAlong(leg, front)[1] <= faceA
        )
        .map((leg) => extentAlong(leg, front)[1]);
    const legFrontB = legBoxes
        .filter(
            (leg) =>
                extentAlong(leg, front)[1] > faceA &&
                extentAlong(leg, front)[0] < frontEdge &&
                extentAlong(leg, out)[1] <= faceB
        )
        .map((leg) => extentAlong(leg, out)[1]);
    const setbacks = [
        ...(legFrontA.length
            ? [faceA - Math.max(...legFrontA) - thickness]
            : []),
        ...(legFrontB.length
            ? [faceB - Math.max(...legFrontB) - thickness]
            : []),
    ];
    const depth = setbacks.length
        ? Math.min(...setbacks)
        : KICK_PANEL_SETBACK_MM * SCENE_UNITS_PER_MM;

    return {front, out, faceA, faceB, depth};
};

/**
 * Where a kick-length panel running along either face of an L-shaped corner
 * base rests in its inside corner: outer face `depth` behind its face, and
 * its corner end on the line where the other face's panel would have its
 * back. A panel along face A and one along face B share that box minimum and
 * differ only in which way they run.
 * @param {THREE.Group} panel The kick-length panel.
 * @param {THREE.Group} cabinet The L-shaped corner base.
 * @param {THREE.Group[]} products Every product in the room.
 * @return {THREE.Vector3|null} The panel's resting origin, or null when the
 * L has no notch or a floor product already fills the corner.
 */
export const getLShapeKickPanelLanding = (
    panel: THREE.Group,
    cabinet: THREE.Group,
    products: THREE.Group[]
) => {
    const {thickness} = panelExtents(panel);
    const corner = getLShapeCorner(cabinet, thickness);

    if (!corner) return null;

    const {front, out, faceA, faceB, depth} = corner;
    const landing = landingForBoxMin(
        panel,
        front,
        faceA - depth - thickness,
        out,
        faceB - depth - thickness
    );

    return landingMeetsFloorProduct(panel, landing, products) ? null : landing;
};

/**
 * Where a kick-length panel rests at a right angle to the end of another
 * kick-length panel. Either joint closes the corner:
 * - it butts against the other panel: its back on the other panel's end
 *   plane, its thickness over that end, its end on the other panel's face,
 *   and its body running away from it. That is the corner an L-shaped base
 *   produces. One candidate for each end and each side of the other panel.
 * - the other panel butts against it: its side on the other panel's end
 *   plane, just past that end, with its own start or end lined up with the
 *   other panel's face so it covers the other panel's end. One candidate for
 *   each end of the other panel and each end of this one.
 * The candidate nearest the drag target wins.
 * @param {THREE.Group} panel The kick-length panel being moved.
 * @param {THREE.Group} other The kick-length panel already placed.
 * @param {THREE.Group[]} products Every product in the room.
 * @param {THREE.Vector3} targetPos The drag target.
 * @return {THREE.Vector3|null} The panel's resting origin, or null when the
 * panels are parallel or every candidate is occupied.
 */
export const getKickPanelJointLanding = (
    panel: THREE.Group,
    other: THREE.Group,
    products: THREE.Group[],
    targetPos: THREE.Vector3
) => {
    const placed = panelExtents(other);
    const moving = panelExtents(panel);

    if (placed.runsAlongX === moving.runsAlongX) return null;

    const along = placed.runsAlongX
        ? new THREE.Vector3(1, 0, 0)
        : new THREE.Vector3(0, 0, 1);
    const side = placed.runsAlongX
        ? new THREE.Vector3(0, 0, 1)
        : new THREE.Vector3(1, 0, 0);
    const otherBox = new THREE.Box3().setFromObject(other);
    const [start, end] = extentAlong(otherBox, along);
    const [near, far] = extentAlong(otherBox, side);
    const [panelStart, panelEnd] = extentAlong(
        new THREE.Box3().setFromObject(panel),
        side
    );
    const length = panelEnd - panelStart;
    const planDistance = (landing: THREE.Vector3) =>
        Math.hypot(landing.x - targetPos.x, landing.z - targetPos.z);

    const buttingOther = [start, end - moving.thickness].flatMap((minAlong) =>
        [far, near - length].map((minSide) =>
            landingForBoxMin(panel, along, minAlong, side, minSide)
        )
    );
    const runningPastEnd = [start - moving.thickness, end].flatMap((minAlong) =>
        [near, far - length].map((minSide) =>
            landingForBoxMin(panel, along, minAlong, side, minSide)
        )
    );
    const candidates = [...buttingOther, ...runningPastEnd]
        .filter(
            (landing) => !landingMeetsFloorProduct(panel, landing, products)
        )
        .sort((a, b) => planDistance(a) - planDistance(b));

    return candidates[0] ?? null;
};

// A landing on the floor as a candidate: the panel keeps its own height, and
// the landing counts only within the snap distance, measured in plan.
const floorCandidate = (
    landing: THREE.Vector3 | null,
    targetPos: THREE.Vector3
): SnapCandidate | null => {
    if (landing === null) return null;

    const distance = computePlanDistance(landing, targetPos);
    if (distance > DEFAULT_SNAPPING_DISTANCE) return null;

    return {
        position: new THREE.Vector3(landing.x, targetPos.y, landing.z),
        distance,
    };
};

// A kick-length panel at a right angle to the end of another one.
export const getKickPanelJointSnap: SnapStrategy = (panel, other, ctx) =>
    floorCandidate(
        getKickPanelJointLanding(panel, other, ctx.productsRef, ctx.targetPos),
        ctx.targetPos
    );

// A kick-length panel into the inside corner of an L-shaped corner base.
export const getLShapeKickPanelSnap: SnapStrategy = (panel, cabinet, ctx) =>
    floorCandidate(
        getLShapeKickPanelLanding(panel, cabinet, ctx.productsRef),
        ctx.targetPos
    );
